// Centralized cache key factory for localStorage
// Windows: 1m, 3m, 6m, 1y, 2y

export const WINDOWS = ["1m", "3m", "6m", "1y", "2y"] as const;
export type WindowKey = typeof WINDOWS[number];

export const CacheKeys = {
  // ---- Portfolio summary ----
  summary: {
    // Portfolio allocations by account (values are PERCENTAGES, not dollars)
    allocationsAccount: () => `portfolio:allocations`,
    // All portfolio windows in one array [{window, pct}]
    windows: () => `portfolio:windows`,
    // Past day pct for the WHOLE portfolio (single number)
    pastDayPct: () => `portfolio:pastDayPct`,
    // Account info with names and values
    accountInfo: () => `portfolio:accountInfo`,
    // Portfolio chart data - all windows in one entry
    chart: () => `portfolio:chart`,
  },

  // ---- Activity streams (event icons) ----
  act: {
    portfolio: () => `act:events:portfolio`,
  },

  // ---- Global ticker data ----
  tickerWindows: () => `tickerWindows`,
  tickerCharts: () => `tickerCharts`,

  // ---- Account window packs ----
  account: {
    // All windows for an account in one array [{window, pct}]
    windows: (acct: string) => `account:windows:${acct}`,
    // Account chart data - all windows in one entry
    chart: (acct: string) => `account:chart:${acct}`,
  },


  tickers: {
    // Account level with all tickers: {AAPL: 10, AAL: 0}
    shares: (acct: string) => `tickers:shares:${acct}`,
    // Account level with all tickers: {AAPL: 20.5, AAL: null}
    myTotal: (acct: string) => `tickers:myTotal:${acct}`,
    // Cost basis time series (price per share) - account level with all tickers
    chartCostBasis: (acct: string) => `tickers:chart:costBasis:${acct}`,
  },

  // ---- Top movers (no separate timestamp key) ----
  topMovers: {
    list: () => `topmovers:list`,
  },

  // ---- Roster: accounts with tickers per window ----
  roster: {
    accounts: () => `roster:accounts`,
  },

  // ---- Chart view toggles ----
  toggles: {
    // Single key for all toggle states: portfolio:toggles
    // Structure: { portfolio_toggle?: true, accounts: { [accountId]: { account_toggle?: true, tickers: { [ticker]: true } } } }
    // Only stores true values (non-default views), omits false/missing (default views)
    all: () => `portfolio:toggles`,
  },

  // ---- Portfolio view preference ----
  portfolio: {
    // Portfolio view as friend tier: 'full', 'tier1', 'tier2', 'tier3', 'basic'
    viewAs: () => `portfolio:viewAs`,
  },

  // ---- Friends portfolio refresh tracking ----
  friends: {
    // Tracks last portfolio update timestamp for each friend visited
    // Structure: { friendId1: "2024-01-01T12:00:00Z", friendId2: "2024-01-02T12:00:00Z" }
    lastPortfolioUpdate: () => `friends:lastPortfolioUpdate`,
  },

  // ---- Friend dashboard selections (per-friend state) ----
  friendDashboard: {
    // Expanded accounts for Gold tier only
    // Structure: { friendId1: [accountId1, accountId2], friendId2: [accountId3] }
    expandedAccounts: () => `friend:expandedAccounts`,
    // Selected account ID per friend
    // Structure: { friendId1: "accountId1", friendId2: "accountId2" }
    selectedAccountId: () => `friend:dashboard_selectedAccountId`,
    // Selected ticker per friend
    // Structure: { friendId1: "AAPL", friendId2: "MSFT" }
    selectedTicker: () => `friend:dashboard_selectedTicker`,
  },

  // ---- Friend portfolio cache keys (with friend:<friend_id>: prefix) ----
  friend: {
    // Friend portfolio summary
    summary: {
      allocationsAccount: (friendId: string) => `friend:${friendId}:portfolio:allocations`,
      windows: (friendId: string) => `friend:${friendId}:portfolio:windows`,
      pastDayPct: (friendId: string) => `friend:${friendId}:portfolio:pastDayPct`,
      accountInfo: (friendId: string) => `friend:${friendId}:portfolio:accountInfo`,
      chart: (friendId: string) => `friend:${friendId}:portfolio:chart`,
    },
    // Friend activity streams
    act: {
      portfolio: (friendId: string) => `friend:${friendId}:act:events:portfolio`,
    },
    // Friend global ticker data
    tickerWindows: (friendId: string) => `friend:${friendId}:tickerWindows`,
    tickerCharts: (friendId: string) => `friend:${friendId}:tickerCharts`,
    // Friend account window packs
    account: {
      windows: (friendId: string, acct: string) => `friend:${friendId}:account:windows:${acct}`,
      chart: (friendId: string, acct: string) => `friend:${friendId}:account:chart:${acct}`,
    },
    // Friend ticker data
    tickers: {
      myTotal: (friendId: string, acct: string) => `friend:${friendId}:tickers:myTotal:${acct}`,
    },
    // Friend top movers
    topMovers: {
      list: (friendId: string) => `friend:${friendId}:topmovers:list`,
    },
    // Friend roster
    roster: {
      accounts: (friendId: string) => `friend:${friendId}:roster:accounts`,
    },
  },

  // ---- Tier-prefixed cache keys (with tier1:, tier2:, tier3: prefix for "View Portfolio As") ----
  tier: {
    // Tier portfolio summary
    summary: {
      allocationsAccount: (tier: string) => `${tier}:portfolio:allocations`,
      windows: (tier: string) => `${tier}:portfolio:windows`,
      pastDayPct: (tier: string) => `${tier}:portfolio:pastDayPct`,
      accountInfo: (tier: string) => `${tier}:portfolio:accountInfo`,
      chart: (tier: string) => `${tier}:portfolio:chart`,
    },
    // Tier activity streams
    act: {
      portfolio: (tier: string) => `${tier}:act:events:portfolio`,
    },
    // Tier global ticker data
    tickerWindows: (tier: string) => `${tier}:tickerWindows`,
    tickerCharts: (tier: string) => `${tier}:tickerCharts`,
    // Tier account window packs
    account: {
      windows: (tier: string, acct: string) => `${tier}:account:windows:${acct}`,
      chart: (tier: string, acct: string) => `${tier}:account:chart:${acct}`,
    },
    // Tier ticker data
    tickers: {
      shares: (tier: string, acct: string) => `${tier}:tickers:shares:${acct}`,
      myTotal: (tier: string, acct: string) => `${tier}:tickers:myTotal:${acct}`,
      chartCostBasis: (tier: string, acct: string) => `${tier}:tickers:chart:costBasis:${acct}`,
    },
    // Tier top movers
    topMovers: {
      list: (tier: string) => `${tier}:topmovers:list`,
    },
    // Tier roster
    roster: {
      accounts: (tier: string) => `${tier}:roster:accounts`,
    },
  },
} as const;
